/**
 * LooperNode Types - Iterative processing workflow node
 *
 * Inspired by Google Breadboard's Looper pattern.
 * Iterates over a list, executing a sub-board for each item.
 *
 * Key concepts:
 * - Takes a list of items as input
 * - Executes a task or sub-board for each item
 * - Shows progress as items are processed
 * - Allows user to pause on failure and choose how to proceed
 *
 * @see https://breadboard-ai.github.io/breadboard/docs/concepts/looper/
 */

// ============================================================================
// STATUS TYPES
// ============================================================================

/**
 * LooperStatus - The current state of loop execution
 *
 * States:
 * - idle: Loop not started, waiting for user to click Start
 * - planning: AI is generating a task plan (if task description provided)
 * - executing: Currently processing items in the list
 * - paused: Stopped because an item failed - user must choose action
 * - complete: All items have been processed successfully
 * - error: A fatal error occurred that stopped the entire loop
 */
export type LooperStatus =
  | 'idle'           // Not started
  | 'planning'       // AI generating task plan
  | 'executing'      // Processing items
  | 'paused'         // Stopped on failure
  | 'complete'       // All items processed
  | 'error';         // Fatal error

/**
 * FailureAction - What the user can choose when an item fails
 *
 * Options:
 * - skip: Ignore this item and continue with the next one
 * - retry: Try processing this item again
 * - abort: Stop the entire loop immediately
 */
export type FailureAction = 'skip' | 'retry' | 'abort';

// ============================================================================
// RESULT TYPES
// ============================================================================

/**
 * IterationResult - The result of processing a single item
 *
 * Captures:
 * - Which item was processed (index and value)
 * - The output from processing it
 * - Any error that occurred
 * - How long it took
 */
export interface IterationResult {
  /** Zero-based index of this item in the input list */
  index: number;
  /** The original item value that was processed */
  item: unknown;
  /** The output from processing this item (if successful) */
  output: unknown;
  /** Error message if this item failed */
  error?: string;
  /** How long processing took in milliseconds */
  durationMs: number;
}

// ============================================================================
// STATE TYPES
// ============================================================================

/**
 * LooperState - The runtime state of the Looper
 *
 * Tracks:
 * - Current status (idle, executing, paused, etc.)
 * - AI-generated plan (if task description was provided)
 * - Progress through the items
 * - Results collected so far
 * - Details about any failure
 */
export interface LooperState {
  /** Current execution status */
  status: LooperStatus;
  /** AI-generated plan for the task (optional - only if task description provided) */
  plan?: string[];
  /** Index of the item currently being processed (0-based) */
  currentIndex: number;
  /** Total number of items to process */
  totalItems: number;
  /** Results from completed iterations */
  completedOutputs: IterationResult[];
  /** Details about the failed item (only present when status is 'paused') */
  failedItem?: {
    /** Index of the item that failed */
    index: number;
    /** The item value that failed */
    item: unknown;
    /** Error message explaining the failure */
    error: string;
  };
}

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * LooperConfig - Configuration for how the Looper operates
 *
 * Controls:
 * - What task to perform on each item
 * - Which sub-board to execute (optional)
 * - Whether to process items sequentially or in parallel
 */
export interface LooperConfig {
  /** Description of what to do with each item (for AI planning) */
  task: string;
  /** ID of a sub-board to execute per item (optional - alternative to task description) */
  subBoardId?: string;
  /** Execute items one at a time (true) or in parallel (false). Default: true */
  sequential: boolean;
}

// ============================================================================
// NODE DATA TYPE
// ============================================================================

/**
 * LooperNodeData - All data stored in a LooperNode
 *
 * This is the shape of `node.data` for nodes with type: 'looper'
 *
 * Contains:
 * - User-editable content (title, config)
 * - Runtime state (execution status, results)
 * - Input items to iterate over
 * - Callback functions (injected by BoardCanvas)
 * - Context references (injected by BoardCanvas)
 */
export interface LooperNodeData {
  // ==================== User Content ====================

  /** Display title for the node */
  title: string;
  /** Configuration for how the loop operates */
  config: LooperConfig;

  // ==================== Runtime State ====================

  /** Current execution state */
  state: LooperState;
  /** Input list to iterate over (connected from upstream node) */
  items: unknown[];

  // ==================== Callbacks ====================
  // These are injected by BoardCanvas at runtime - not stored in Yjs

  /** Called when user changes the title */
  onTitleChange?: (title: string) => void;
  /** Called when user changes configuration */
  onConfigChange?: (config: LooperConfig) => void;
  /** Called when user clicks Start button */
  onStart?: () => void;
  /** Called when user clicks Pause button */
  onPause?: () => void;
  /** Called when user chooses how to handle a failure */
  onResume?: (action: FailureAction) => void;

  // ==================== Context References ====================
  // These are injected by BoardCanvas at runtime - not stored in Yjs

  /** Yjs document for real-time sync */
  ydoc?: unknown;
  /** Board ID for API calls */
  boardId?: string;
  /** Reference to the nodesMap for creating output nodes */
  nodesMap?: unknown;

  // Index signature required for React Flow compatibility
  [key: string]: unknown;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default state for a new LooperNode
 */
export const DEFAULT_LOOPER_STATE: LooperState = {
  status: 'idle',
  currentIndex: 0,
  totalItems: 0,
  completedOutputs: [],
};

/**
 * Default config for a new LooperNode
 */
export const DEFAULT_LOOPER_CONFIG: LooperConfig = {
  task: '',
  sequential: true,
};

/**
 * Creates default data for a new LooperNode
 */
export function createDefaultLooperNodeData(): LooperNodeData {
  return {
    title: 'Loop',
    config: { ...DEFAULT_LOOPER_CONFIG },
    state: { ...DEFAULT_LOOPER_STATE },
    items: [],
  };
}
